import axiosCore from 'axios';
import aceUIError from './error.js';

const DEBUG = false;

let axios = axiosCore;

/**
 * @typedef {Object} AceUIRequestResponse
 */

/**
 * A module to make http requests, particularly to ace web services
 * @module ace-request
 */

/**
 * @alias module:default
 * @param {Object} options
 * @param {string} options.url - The full url to a web resource
 * @param {string} [options.method] - The request method, if omitted it defaults to 'GET'.
 * @param {string} [options.token] - A value to be set as the value of the 'x-auth-token' header.
 * @param {string} [options.matcher] - A value to be set as the value of the 'if-match' header.
 * @param {Object} [options.headers] - Headers to be sent with the request. More explicit values will overwrite
 *  values set in this object (e.g. if 'token' is provided as an option it will
 *  replace any value of the 'x-auth-token' header present in this object).
 * @param {Object} [options.body] - Any content to be sent with the request (should be JSON parsable)
 * @returns {Promise<AceUIRequestResponse>}
 * @throws {AceUIError}
 */
export default async (options, adaptedAxios) => {
  DEBUG && console.log('DEBUG: ACE-REQUEST', options);
  DEBUG && adaptedAxios && console.log('DEBUG: ACE-REQUEST using adapted axios');

  axios = adaptedAxios || axios;

  if (!options || !options.url) {
    throw aceUIError.Error ({
      options,
      origin: 'ace-request',
      type: 'invalidParameters',
      cause: 'Could not make request.'
    });
  }

  let opts = {
    url: options.url,
    headers: options.headers || {}
  };

  if (options.method) {
    opts.method = options.method;
  }

  if (options.body) {
    opts.data = options.body;
  }

  if (options.token) {
    opts.headers['x-auth-token'] = options.token;
  }

  if (options.matcher) {
    if (Array.isArray (options.matcher)) {
      opts.headers['if-match'] = options.matcher.join ();
    } else {
      opts.headers['if-match'] = options.matcher;
    }
  }

  opts.headers['content-type'] = 'application/json';

  try {
    let response = await axios (opts);

    return {
      body: response.data,
      options,
      status: response.status,
      statusText: response.statusText,
      matcher: response.headers['ETag']
    };
  } catch (error) {
    if (error.response) {
      throw aceUIError.Error ({
        cause: error.response.data,
        origin: 'ace-request',

        rootCause: {
          status: error.response.status,
          statusText: error.response.statusText,
          request: error.response.request,
          aceRequestParam: options
        },

        type: aceUIError.types.REQUEST_FAILED,
        severity: aceUIError.severities.MAJOR
      });
    } else if (error.request) {
      throw aceUIError.Error ({
        cause: 'No response',
        origin: 'ace-request',

        rootCause: {
          status: 0,
          statusText: '',
          request: error.request
        },

        severity: aceUIError.severities.MAJOR,
        type: aceUIError.types.REQUEST_FAILED
      });
    } else {
      throw aceUIError.Error ({
        rootCause: error,
        origin: 'ace-request',
        cause: 'Error when processing response.',
        type: aceUIError.types.REQUEST_FAILED,
        severity: aceUIError.severities.MAJOR
      });
    }
  }
};
